/**
 * Конфигурация основных (не доп.) полей форм по типам сущностей.
 * key — имя поля в API, label — подпись, type — text|number|date|boolean|select|entity_ref.
 * entity_ref: { entity: 'clients'|'objects'|... } для подгрузки списка из API.
 */
export const ENTITY_TYPES = ['clients', 'contacts', 'objects', 'contracts', 'stages', 'budgets', 'acts']

/** id в path-сегменте API (UUID, стадии со слэшами и т.д.); Flask декодирует %XX. */
function encodePathId(id) {
  if (id == null || id === '') return id
  return encodeURIComponent(String(id))
}

export const BASE_FIELDS = {
  clients: [
    { key: 'name', label: 'Название', type: 'text', required: true },
    { key: 'inn', label: 'ИНН', type: 'text' },
    { key: 'ogrn', label: 'ОГРН', type: 'text' },
    { key: 'type', label: 'Тип клиента', type: 'text' },
    { key: 'address', label: 'Адрес', type: 'text' }, // подсказки Dadata в форме
    { key: 'city', label: 'Город', type: 'text' },   // заполняется из Dadata или вручную
    { key: 'comments', label: 'Комментарии', type: 'textarea' },
    { key: 'website', label: 'Сайт', type: 'text' },
    { key: 'kp_link', label: 'Ссылка на КП', type: 'text' },
    { key: 'kp_sent_at', label: 'Дата отправки КП', type: 'date' },
    { key: 'status', label: 'Статус', type: 'reference', referenceType: 'клиент_статус' },
  ],
  contacts: [
    { key: 'full_name', label: 'ФИО', type: 'text', required: true },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'phone', label: 'Телефон', type: 'text' },
    { key: 'role', label: 'Роль', type: 'text' },
    { key: 'telegram', label: 'Telegram', type: 'text' },
  ],
  objects: [
    { key: 'name', label: 'Название', type: 'text', required: true },
    { key: 'client_id', label: 'Клиент', type: 'entity_ref', entityRef: 'clients', required: true },
    { key: 'address', label: 'Адрес', type: 'text' }, // подсказки Dadata в форме
    { key: 'city', label: 'Город', type: 'text' },
    { key: 'type', label: 'Тип объекта', type: 'select', options: [{ value: 'Жилой', label: 'Жилой' }, { value: 'Коммерческий', label: 'Коммерческий' }, { value: 'Другое', label: 'Другое' }] },
    { key: 'cadastral_number', label: 'Кадастровый номер', type: 'text' },
    { key: 'description', label: 'Описание', type: 'textarea' },
    { key: 'living_area', label: 'Площадь (м²)', type: 'number', required: true },
    { key: 'parking_spaces', label: 'Машиномест', type: 'number', required: true },
    { key: 'estimated_square_price', label: 'Цена за м²', type: 'number' },
    { key: 'estimated_parking_price', label: 'Цена за м/место', type: 'number' },
    { key: 'estimated_contract_price', label: 'Стоимость с НДС', type: 'number' },
    { key: 'estimated_contract_price_no_vat', label: 'Стоимость без НДС', type: 'number' },
    { key: 'kp_links', label: 'Ссылка на КП', type: 'text' },
  ],
  contracts: [
    { key: 'number', label: 'Номер договора', type: 'text', required: true },
    { key: 'client_id', label: 'Клиент', type: 'entity_ref', entityRef: 'clients' },
    { key: 'object_id', label: 'Объект', type: 'entity_ref', entityRef: 'objects', filterByRef: 'client_id' },
    { key: 'contract_date', label: 'Дата заключения', type: 'date' },
    { key: 'total_amount', label: 'Сумма', type: 'number', hidden: true },
    { key: 'status', label: 'Статус', type: 'reference', referenceType: 'статусы_договоров', defaultValue: 'новый',
      referenceOrder: ['новый', 'в работе', 'приостановлен', 'завершен', 'отменен'] },
    { key: 'budget_status', label: 'Статус бюджета', type: 'reference', referenceType: 'статусы_бюджета_договора', defaultValue: 'новый',
      referenceOrder: ['новый', 'в согласовании', 'согласован'] },
    { key: 'google_drive_link', label: 'Ссылка на договор', type: 'text' },
    { key: 'technical_spec_link', label: 'Ссылка на ТЗ', type: 'text' },
    { key: 'prepayment', label: 'Сумма предоплаты', type: 'number' },
    { key: 'prepayment_date', label: 'Дата предоплаты', type: 'date' },
    { key: 'result_folder_link', label: 'Ссылка на папку результата', type: 'text' },
  ],
  stages: [
    { key: 'object_stage_id', label: 'Тип этапа', type: 'entity_ref', entityRef: 'object_stages', required: true },
    { key: 'distribution_type', label: 'Тип распределения', type: 'reference', referenceType: 'тип распределения', required: true },
    { key: 'base_percentage_group_id', label: 'Группа базовых процентов', type: 'percentage_group_ref' },
    { key: 'cost', label: 'Стоимость к распределению', type: 'number', hint: 'Фактическая стоимость стадии — сколько средств будет распределено на сотрудников', group: 'cost_pair' },
    { key: 'cost_no_vat', label: 'Стоимость за вычетом налогов', type: 'number', readOnly: true, hint: 'Рассчитывается автоматически от стоимости за вычетом НДС', group: 'cost_pair' },
    { key: 'contract_cost', label: 'Стоимость по договору', type: 'number', hint: 'Стоимость стадии, которая указывается в договоре для клиента', group: 'contract_cost_pair' },
    { key: 'contract_cost_no_vat', label: 'Стоимость по договору за вычетом налогов', type: 'number', readOnly: true, hint: 'Стоимость в договоре за вычетом НДС', group: 'contract_cost_pair' },
    { key: 'planned_date', label: 'Плановая дата', type: 'date' },
    { key: 'fact_date', label: 'Срок по договору', type: 'date' },
    { key: 'completion_date', label: 'Дата завершения', type: 'date' },
    { key: 'status', label: 'Статус', type: 'select', options: [{ value: 'не в работе', label: 'не в работе' }, { value: 'в работе', label: 'в работе' }, { value: 'работы завершены', label: 'работы завершены' }, { value: 'закрыта', label: 'закрыта' }] },
  ],
  budgets: [
    { key: 'name', label: 'Имя бюджета', type: 'text', required: true },
    { key: 'contract_id', label: 'Договор', type: 'entity_ref', entityRef: 'contracts', required: true },
    { key: 'stage_id', label: 'Этап', type: 'entity_ref', entityRef: 'stages', required: true },
    { key: 'distribution_type', label: 'Тип распределения', type: 'reference', referenceType: 'тип распределения', required: true },
    { key: 'amount_with_vat', label: 'Сумма с НДС', type: 'number', group: 'amount_pair' },
    { key: 'amount_without_vat', label: 'Сумма без НДС', type: 'number', readOnly: true, group: 'amount_pair' },
    { key: 'prev_actuated_amount', label: 'Заактировано ранее', type: 'number' },
    { key: 'base_percentage_group_id', label: 'Группа базовых процентов', type: 'percentage_group_ref' },
    { key: 'approval_status', label: 'Статус согласования', type: 'select', options: [{ value: 'новый', label: 'новый' }, { value: 'в согласовании', label: 'в согласовании' }, { value: 'согласован', label: 'согласован' }] },
  ],
  acts: [
    { key: 'payment_type', label: 'Тип оплаты', type: 'select', defaultValue: 'bank_transfer', options: [{ value: 'bank_transfer', label: 'Безналичный' }, { value: 'cash', label: 'Наличные' }, { value: 'artel', label: 'Средства артели' }] },
    { key: 'number', label: 'Номер акта', type: 'text', required: false, title: 'Можно оставить пустым, заполнится автоматически' },
    { key: 'budget_id', label: 'Бюджет', type: 'entity_ref', entityRef: 'budgets', contextKey: 'contractId' },
    { key: 'amount', label: 'Сумма', type: 'number', required: true, hint: 'Сумма акта до вычета налогов', group: 'amount_pair' },
    { key: 'amount_after_tax', label: 'Сумма за вычетом налогов', type: 'number', readOnly: true, required: true, hint: 'Рассчитывается автоматически от суммы за вычетом НДС — именно эта сумма распределяется на сотрудников', group: 'amount_pair' },
    { key: 'issue_date', label: 'Дата выставления', type: 'date' },
    { key: 'status', label: 'Статус', type: 'select', options: [{ value: 'не подписан', label: 'не подписан' }, { value: 'на подписании', label: 'на подписании' }, { value: 'подписан', label: 'подписан' }, { value: 'оплачен', label: 'оплачен' }] },
  ],
}

export function getBaseFields(entityType) {
  return BASE_FIELDS[entityType] || []
}

export function getEntityListUrl(entityType, context = {}) {
  const base = '/api/company'
  switch (entityType) {
    case 'clients':
      return `${base}/clients`
    case 'contacts':
      return context.clientId ? `${base}/clients/${encodePathId(context.clientId)}/contacts` : null
    case 'objects':
      return `${base}/objects`
    case 'contracts':
      return `${base}/contracts`
    case 'stages':
      if (context.objectId) return `${base}/objects/${encodePathId(context.objectId)}/stages`
      return context.contractId ? `${base}/contracts/${encodePathId(context.contractId)}/stages` : null
    case 'budgets': {
      const params = []
      if (context.contractId) params.push(`contract_id=${encodeURIComponent(context.contractId)}`)
      if (context.objectId) params.push(`object_id=${encodeURIComponent(context.objectId)}`)
      if (context.stageId) params.push(`stage_id=${encodeURIComponent(context.stageId)}`)
      return `${base}/budgets${params.length ? `?${params.join('&')}` : ''}`
    }
    case 'acts':
      return context.contractId ? `${base}/contracts/${encodePathId(context.contractId)}/acts` : null
    default:
      return null
  }
}

export function getEntityDetailUrl(entityType, id, context = {}) {
  const base = '/api/company'
  switch (entityType) {
    case 'clients':
      return `${base}/clients/${encodePathId(id)}`
    case 'contacts':
      return `${base}/contacts/${encodePathId(id)}`
    case 'objects':
      return `${base}/objects/${encodePathId(id)}`
    case 'contracts':
      return `${base}/contracts/${encodePathId(id)}`
    case 'stages':
      if (context.objectStage) return `${base}/object-stages/${encodePathId(id)}`
      return `${base}/stages/${encodePathId(id)}`
    case 'budgets':
      return `${base}/budgets/${encodePathId(id)}`
    case 'acts':
      return `${base}/acts/${encodePathId(id)}`
    default:
      return null
  }
}

export function getEntityCreateUrl(entityType, context = {}) {
  const base = '/api/company'
  switch (entityType) {
    case 'clients':
      return { method: 'POST', url: `${base}/clients` }
    case 'contacts':
      return context.clientId ? { method: 'POST', url: `${base}/clients/${encodePathId(context.clientId)}/contacts` } : null
    case 'objects':
      return { method: 'POST', url: `${base}/objects` }
    case 'contracts':
      return { method: 'POST', url: `${base}/contracts` }
    case 'stages':
      if (context.objectId) return { method: 'POST', url: `${base}/objects/${encodePathId(context.objectId)}/stages` }
      return context.contractId ? { method: 'POST', url: `${base}/contracts/${encodePathId(context.contractId)}/stages` } : null
    case 'budgets':
      return { method: 'POST', url: `${base}/budgets` }
    case 'acts':
      return context.contractId ? { method: 'POST', url: `${base}/contracts/${encodePathId(context.contractId)}/acts` } : null
    default:
      return null
  }
}

export function getEntityUpdateUrl(entityType, id, context = {}) {
  const base = '/api/company'
  switch (entityType) {
    case 'clients':
      return { method: 'PUT', url: `${base}/clients/${encodePathId(id)}` }
    case 'contacts':
      return { method: 'PUT', url: `${base}/contacts/${encodePathId(id)}` }
    case 'objects':
      return { method: 'PUT', url: `${base}/objects/${encodePathId(id)}` }
    case 'contracts':
      return { method: 'PUT', url: `${base}/contracts/${encodePathId(id)}` }
    case 'stages':
      if (context.objectStage) return { method: 'PUT', url: `${base}/object-stages/${encodePathId(id)}` }
      return { method: 'PUT', url: `${base}/stages/${encodePathId(id)}` }
    case 'budgets':
      return { method: 'PUT', url: `${base}/budgets/${encodePathId(id)}` }
    case 'acts':
      return { method: 'PUT', url: `${base}/acts/${encodePathId(id)}` }
    default:
      return null
  }
}

export function getEntityDeleteUrl(entityType, id) {
  const base = '/api/company'
  switch (entityType) {
    case 'clients':
      return { method: 'DELETE', url: `${base}/clients/${encodePathId(id)}` }
    case 'contacts':
      return { method: 'DELETE', url: `${base}/contacts/${encodePathId(id)}` }
    case 'objects':
      return { method: 'DELETE', url: `${base}/objects/${encodePathId(id)}` }
    case 'contracts':
      return { method: 'DELETE', url: `${base}/contracts/${encodePathId(id)}` }
    case 'stages':
      return { method: 'DELETE', url: `${base}/stages/${encodePathId(id)}` }
    case 'budgets':
      return { method: 'DELETE', url: `${base}/budgets/${encodePathId(id)}` }
    case 'acts':
      return { method: 'DELETE', url: `${base}/acts/${encodePathId(id)}` }
    default:
      return null
  }
}
